import { Component, OnInit, Input, SimpleChanges, forwardRef } from '@angular/core';
import { KeyValuePipe } from '@angular/common';
import { IonSpinner, IonIcon } from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { ReactiveFormsModule, ControlContainer, FormGroupDirective, FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

import { 
    MarvSectionTitleComponent, MarvToggleComponent, MarvSelectComponent, MarvSelectItem, MarvCheckboxComponent,
    MarvItemComponent, MarvSubItemComponent, MarvButtonComponent 
} from '@marv/components';
import { PhotoFiltersValues, AnimaPhotoFiltersValues } from '@app/components';
import { PhotoService } from '@app/services';
import { 
    OperationCard, AnimaPhotoSearchCriteria, PhotoSearchOption, IsaReportDataModelGroup, AnimaPhotoSearchProductCategory,
    AnimaPhotoSearchProductRange 
} from '@app/models';

export class AnimaOperationFilters {
    operation: OperationCard;
    filters: AnimaPhotoSearchCriteria;
    open: boolean = false;
}

@Component({
    selector: 'app-photo-gallery-filters-anima',
    templateUrl: './anima.component.html',
    styleUrls: ['./anima.component.scss'],    
    imports: [
        IonSpinner, IonIcon, TranslatePipe, KeyValuePipe, 
        forwardRef(() => MarvSectionTitleComponent), forwardRef(() => MarvToggleComponent), forwardRef(() => MarvSelectComponent), 
        forwardRef(() => MarvCheckboxComponent), forwardRef(() => MarvItemComponent), forwardRef(() => MarvSubItemComponent), 
        forwardRef(() => MarvButtonComponent)
    ],
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: FormGroupDirective
        }
    ]
})
export class PhotoGalleryFiltersAnimaComponent  implements OnInit {

    @Input({alias : 'operationsAll', required : true}) operationsAll: Array<OperationCard> = [];
    @Input({alias : 'photoFiltersValues', required : true}) photoFiltersValues: PhotoFiltersValues;

    isFetching: boolean = false;
    operationsSubscription: Subscription;
    operationsLoga: Array<OperationCard> = [];
    operationFilters: Map<number, AnimaOperationFilters> = new Map();
    
    photoTagAllChecked: Map<number, boolean> = new Map();
    isaReportPicturesAllChecked: Map<number, boolean> = new Map();
    isaReportGroupAllChecked: Map<string, boolean> = new Map();
    photoProductQuestionAllChecked: Map<number, boolean> = new Map();
    photoQuestionAllChecked: Map<number, boolean> = new Map();
    productCategoryAllChecked: Map<string, boolean> = new Map();
    productRangeAllChecked: Map<string, boolean> = new Map();
    
    constructor(
        public formGroupDirective: FormGroupDirective,
        private photoService: PhotoService
    ) { }

    async ngOnInit() {
        
        await this.loadOperations();        
        
        this.operationsSubscription = this.formGroupDirective.form.controls['operationsIds'].valueChanges.subscribe(async (newValue) => {
            await this.loadOperations();
        });
                
    }
       
    ngOnDestroy() {
        if(this.operationsSubscription){
            this.operationsSubscription.unsubscribe();
        } 
    }
    
    async loadOperations(){     
    
        this.isFetching = true;
       
        let operationsIds: Array<number> = [];
        let operationFilters: Map<number, AnimaOperationFilters> = new Map();
        let oldOperationFilters = this.operationFilters;
        this.operationsLoga = [];
        
        if(this.formGroupDirective.form.controls['operationsIds'].value){
            this.formGroupDirective.form.controls['operationsIds'].value.forEach((o: string) => {
                let opInfos = o.split('|');
                if(opInfos.length === 2 && opInfos[0] === 'loga'){
                    operationsIds.push(parseInt(opInfos[1]));
                }
            });
        }
                
        this.operationsAll.forEach(o => {
            if(o.type === 'loga' && (operationsIds.length === 0 || operationsIds.indexOf(o.id) !== -1)){
                this.operationsLoga.push(o);
            }            
        });
        
        if(this.operationsLoga.length > 0){  
            for(let op of this.operationsLoga){
                let photoSearchCriteria = await this.photoService.loadAnimaSearchCriteria(op.id);
                if(photoSearchCriteria != null){
                    operationFilters.set(op.id, {operation: op, filters: photoSearchCriteria, open: false});
                }
            }
        }
        
        this.setAnimaFiltersControls(operationFilters, oldOperationFilters);        
        this.operationFilters = operationFilters;
        
        this.isFetching = false;
    }
    
    toogleOperationFilters(opFilters: AnimaOperationFilters){
        let open = !opFilters.open;
        if(open){
            this.operationFilters.forEach((opFilters) => {
                opFilters.open = false;
            });
        }
        opFilters.open = open;        
    }
        
    setAnimaFiltersControls(newPhotoSearchCriterias: Map<number, AnimaOperationFilters>, oldPhotoSearchCriterias: Map<number, AnimaOperationFilters>) {         
              
        this.photoTagAllChecked.clear();
        this.isaReportPicturesAllChecked.clear();
        this.isaReportGroupAllChecked.clear();
        this.photoProductQuestionAllChecked.clear();
        this.photoQuestionAllChecked.clear();
        this.productCategoryAllChecked.clear();
        this.productRangeAllChecked.clear();
                              
        newPhotoSearchCriterias.forEach((opFilter, index) => {
            if(!oldPhotoSearchCriterias.has(index)){
                
                this.photoTagAllChecked.set(index, false);
                this.isaReportPicturesAllChecked.set(index, false);
                this.photoProductQuestionAllChecked.set(index, false);
                this.photoQuestionAllChecked.set(index, false);
                
                let valuesFilters = this.photoFiltersValues.animaPhotoFiltersValues.get(index);
                
                this.formGroupDirective.form.addControl('anima_enableTaggedPictures_'+index, new FormControl(valuesFilters?.skipTaggedPictures !== true), {emitEvent: false});   
                this.formGroupDirective.form.addControl('anima_enableIsaReportPictures_'+index, new FormControl(valuesFilters?.skipIsaReportPictures !== true), {emitEvent: false});   
                this.formGroupDirective.form.addControl('anima_enableProductPictures_'+index, new FormControl(valuesFilters?.skipProductPictures !== true), {emitEvent: false});   
                
                if(opFilter.filters.photoTagCriteriaChunks?.length > 0){
                    for(let rows of opFilter.filters.photoTagCriteriaChunks){
                        for(let tag of rows){                            
                            let check = valuesFilters?.photoTags && valuesFilters.photoTags.indexOf(tag.value) !== -1;
                            this.formGroupDirective.form.addControl('anima_photoTag_'+index+'_'+tag.value, new FormControl(check), {emitEvent: false});   
                        }
                    }
                }
                
                if(opFilter.filters.isaReportDataModelGroups?.length > 0){
                    for(let group of opFilter.filters.isaReportDataModelGroups){
                        this.isaReportGroupAllChecked.set(index+'_'+group.id, false);
                        for(let rows of group.isaReportDataModelChunks){
                            for(let question of rows){
                                let check = valuesFilters?.isaReportQuestions && valuesFilters.isaReportQuestions.indexOf(question.id) !== -1;
                                this.formGroupDirective.form.addControl('anima_photoQuestion_'+index+'_'+question.id, new FormControl(check), {emitEvent: false});   
                            }
                        }
                    }
                }
                
                if(opFilter.filters.productDataModelsChunks?.length > 0){
                    for(let rows of opFilter.filters.productDataModelsChunks){
                        for(let question of rows){
                            let check = valuesFilters?.productQuestions && valuesFilters.productQuestions.indexOf(question.id) !== -1;
                            this.formGroupDirective.form.addControl('anima_photoProductQuestion_'+index+'_'+question.value, new FormControl(check), {emitEvent: false});   
                        }
                    }
                }
                
                if(opFilter.filters.productCategories?.length > 0){
                    for(let category of opFilter.filters.productCategories){
                        this.productCategoryAllChecked.set(index+'_'+category.id, false);
                        for(let range of category.ranges){
                            this.productRangeAllChecked.set(index+'_'+range.id, false);
                            for(let rows of range.productChunks){
                                for(let product of rows){
                                    let check = valuesFilters?.products && valuesFilters.products.indexOf(product.id) !== -1;
                                    this.formGroupDirective.form.addControl('anima_photoProduct_'+index+'_'+product.id, new FormControl(check), {emitEvent: false});   
                                }
                            }
                        }
                    }
                }
                
            }
            else{
                oldPhotoSearchCriterias.delete(index);
            }
        });
                
        oldPhotoSearchCriterias.forEach((opFilter, index) => {
            
            this.formGroupDirective.form.removeControl('anima_skipTaggedPictures_'+index, {emitEvent: false});  
            this.formGroupDirective.form.removeControl('anima_enableIsaReportPictures_'+index, {emitEvent: false});  
            this.formGroupDirective.form.removeControl('anima_enableProductPictures_'+index, {emitEvent: false});  
            
            if(opFilter.filters.photoTagCriteriaChunks?.length > 0){
                for(let rows of opFilter.filters.photoTagCriteriaChunks){
                    for(let tag of rows){
                        this.formGroupDirective.form.removeControl('anima_photoTag_'+index+'_'+tag.value, {emitEvent: false});   
                    }
                }
            }
            
            if(opFilter.filters.isaReportDataModelGroups?.length > 0){
                for(let group of opFilter.filters.isaReportDataModelGroups){
                    for(let rows of group.isaReportDataModelChunks){
                        for(let question of rows){
                            this.formGroupDirective.form.removeControl('anima_photoQuestion_'+index+'_'+question.id, {emitEvent: false});   
                        }
                    }
                }
            }            
            
            if(opFilter.filters.productDataModelsChunks?.length > 0){
                for(let rows of opFilter.filters.productDataModelsChunks){
                    for(let question of rows){
                        this.formGroupDirective.form.removeControl('anima_photoProductQuestion_'+index+'_'+question.value, {emitEvent: false});   
                    }
                }
            }
            
            if(opFilter.filters.productCategories?.length > 0){
                for(let category of opFilter.filters.productCategories){
                    for(let range of category.ranges){
                        for(let rows of range.productChunks){
                            for(let product of rows){
                                this.formGroupDirective.form.removeControl('anima_photoProduct_'+index+'_'+product.id, {emitEvent: false});   
                            }
                        }
                    }
                }
            }
            
        });
        
    }
    
    toggleCheckVisitPhotos(operationId: number, photoTagCriteriaChunks: PhotoSearchOption[][]) {
        let check = !(this.photoTagAllChecked.get(operationId) || false);
        for(let rows of photoTagCriteriaChunks){
            for(let tag of rows){
                this.formGroupDirective.form.controls['anima_photoTag_'+operationId+'_'+tag.value].setValue(check, {emitEvent: false});   
            }
        }
        this.photoTagAllChecked.set(operationId, check);
    }
    
    toggleCheckIsaReportPictures(operationId: number, isaReportDataModelGroups: Array<IsaReportDataModelGroup>){
        let check = !(this.isaReportPicturesAllChecked.get(operationId) || false);
        for(let group of isaReportDataModelGroups){
            this.isaReportGroupAllChecked.set(operationId+'_'+group.id, check);
            for(let rows of group.isaReportDataModelChunks){
                for(let question of rows){
                    this.formGroupDirective.form.controls['anima_photoQuestion_'+operationId+'_'+question.id].setValue(check, {emitEvent: false});   
                }
            }
        }
        this.isaReportPicturesAllChecked.set(operationId, check);
    }
    
    toggleCheckIsaReportGroup(operationId: number, groupId: number, isaReportDataModelChunks: Array<Array<any>>){
        let key = operationId+'_'+groupId;
        let check = !(this.isaReportGroupAllChecked.get(key) || false);       
        for(let rows of isaReportDataModelChunks){
            for(let question of rows){
                this.formGroupDirective.form.controls['anima_photoQuestion_'+operationId+'_'+question.id].setValue(check, {emitEvent: false});   
            }
        }        
        this.isaReportGroupAllChecked.set(key, check);
    }   
    
    toggleCheckPhotoProductQuestion(operationId: number, productDataModelsChunks: any[][]) {
        let check = !(this.photoProductQuestionAllChecked.get(operationId) || false);
        for(let rows of productDataModelsChunks){
            for(let question of rows){
                this.formGroupDirective.form.controls['anima_photoProductQuestion_'+operationId+'_'+question.value].setValue(check, {emitEvent: false}); 
            }
        }
        this.photoProductQuestionAllChecked.set(operationId, check);
    }
    
    toggleCheckPhotoQuestion(operationId: number, productCategories: Array<AnimaPhotoSearchProductCategory>) {
        let check = !(this.photoQuestionAllChecked.get(operationId) || false);
        for(let category of productCategories){
            this.productCategoryAllChecked.set(operationId+'_'+category.id, check);
            for(let range of category.ranges){
                this.productRangeAllChecked.set(operationId+'_'+range.id, check);
                for(let rows of range.productChunks){
                    for(let product of rows){
                        this.formGroupDirective.form.controls['anima_photoQuestion_'+operationId+'_'+product.id].setValue(check, {emitEvent: false}); 
                    }
                }
            }
        }
        this.photoQuestionAllChecked.set(operationId, check);
    }
    
    toggleCheckProductCategory(operationId: number, categoryId: number, ranges: Array<AnimaPhotoSearchProductRange>) {
        let key = operationId+'_'+categoryId;
        let check = !(this.productCategoryAllChecked.get(key) || false);       
        for(let range of ranges){
            this.productRangeAllChecked.set(operationId+'_'+range.id, check);
            for(let rows of range.productChunks){
                for(let product of rows){
                    this.formGroupDirective.form.controls['anima_photoQuestion_'+operationId+'_'+product.id].setValue(check, {emitEvent: false}); 
                }
            }
        }        
        this.productCategoryAllChecked.set(key, check);
    }
    
    toggleCheckProductRange(operationId: number, rangeId: number, productChunks: Array<Array<any>>) {
        let key = operationId+'_'+rangeId;
        let check = !(this.productRangeAllChecked.get(key) || false); 
        for(let rows of productChunks){
            for(let product of rows){
                this.formGroupDirective.form.controls['anima_photoQuestion_'+operationId+'_'+product.id].setValue(check, {emitEvent: false}); 
            }
        }                
        this.productRangeAllChecked.set(key, check);
    }
}
